import { useMemo } from "react";
import * as Yup from "yup";
import { QuestionConfig } from "./FormFieldRenderer";
import { yupRules } from "@portal/app/_common/lib/yupValidationRules";
import { DateTime } from "luxon";
import { logger } from "@portal/app/_common/lib/logger";

export const useFormFunnelValidation = (questions: QuestionConfig[]) => {
  return useMemo(() => {
    const shape: Record<string, Yup.AnySchema> = {};

    questions.forEach((question) => {
      let schema: Yup.AnySchema;

      switch (question.type) {
        case "email":
          schema = yupRules.optional.email;
          break;
        case "phone":
          schema = yupRules.optional.phone;
          break;
        case "number":
          schema = Yup.number().typeError("Must be a number");
          break;
        case "date":
          schema = Yup.mixed().test("is-date", "Invalid date", (value) => {
             if (!value) return true;
             if (typeof value === "object" && value !== null && "isValid" in value) {
                 return (value as DateTime).isValid;
             }
             if (typeof value === "string") {
                return DateTime.fromISO(value).isValid;
             }
             return false;
          });
          break;
        case "multiselect":
          schema = Yup.array().of(Yup.string().required()).ensure();
          break;
        case "text":
        case "textarea":
        case "select":
        case "radio":
        default:
          schema = Yup.string();
          break;
      }

      // Apply CMS validation rules (pattern, minLength, maxLength) for string-based schemas
      if (question.validation && schema instanceof Yup.StringSchema) {
        let strSchema = schema as Yup.StringSchema;

        if (question.validation.pattern) {
          try {
            const regex = new RegExp(question.validation.pattern);
            strSchema = strSchema.matches(regex, question.validation.customError || "Invalid format");
          } catch (e) {
            logger.warn(
              `[useFormFunnelValidation] Invalid regex pattern from CMS: "${question.validation.pattern}", skipping pattern validation`
            );
          }
        }

        if (question.validation.minLength) {
          strSchema = strSchema.min(
            question.validation.minLength,
            question.validation.customError || `Must be at least ${question.validation.minLength} characters`
          );
        }

        if (question.validation.maxLength) {
          strSchema = strSchema.max(
            question.validation.maxLength,
            question.validation.customError || `Must be at most ${question.validation.maxLength} characters`
          );
        }

        schema = strSchema;
      }

      if (question.required) {
        schema =
          question.type === "multiselect"
            ? (schema as Yup.ArraySchema<string[], Yup.AnyObject, string[], "">).min(
                1,
                `${question.label} is required`
              )
            : schema.required(`${question.label} is required`);
      } else {
        if (question.type !== "email" && question.type !== "phone") {
             schema = schema.nullable().optional();
        }
      }

      shape[question.id] = schema;
    });

    return Yup.object().shape(shape);
  }, [questions]);
};
